import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    getUploadHighlights, getClipQuota, createUploadClips, getUploadClips,
    HighlightCandidate, ClipQuota, UploadClipJob,
} from "../../services/UserDemoService";
import { HighlightClip } from "../../models/HighlightClip";
import styles from "./UserDemos.module.css";

interface Props {
    uploadId: string;
    ownSteamId?: string;
}

const isPending = (j: UploadClipJob) => j.status !== "completed" && j.status !== "failed";

// Short label + colour class per highlight type.
const shortType = (t: string) =>
    t === "Ace" ? "ACE" : t === "QuadKill" ? "4K" : t === "TripleKill" ? "3K" : t === "DoubleKill" ? "2K" : t.replace("Clutch", "");
const typeClass = (t: string) =>
    t === "Ace" ? styles.hlAce : t === "QuadKill" ? styles.hlQuad : t === "TripleKill" ? styles.hlTriple : t === "DoubleKill" ? styles.hlDouble : styles.hlClutch;

const ClipCreatorPanel: React.FC<Props> = ({ uploadId, ownSteamId }) => {
    const { t } = useTranslation("pages");
    const [candidates, setCandidates] = useState<HighlightCandidate[]>([]);
    const [quota, setQuota] = useState<ClipQuota | null>(null);
    const [clips, setClips] = useState<HighlightClip[]>([]);
    const [jobs, setJobs] = useState<UploadClipJob[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [justOrdered, setJustOrdered] = useState(0);
    const successRef = useRef<HTMLDivElement | null>(null);

    const loadClips = useCallback(async () => {
        try {
            const c = await getUploadClips(uploadId);
            setClips(c.clips);
            setJobs(c.jobs);
        } catch { /* keep prior state */ }
    }, [uploadId]);

    useEffect(() => {
        (async () => {
            try {
                const [cand, q] = await Promise.all([getUploadHighlights(uploadId), getClipQuota()]);
                setCandidates(cand);
                setQuota(q);
                await loadClips();
            } catch {
                setError(t("userDemos.clips.loadError"));
            } finally {
                setLoading(false);
            }
        })();
    }, [uploadId, loadClips, t]);

    useEffect(() => {
        if (!jobs.some(isPending)) return;
        const id = setInterval(loadClips, 5000);
        return () => clearInterval(id);
    }, [jobs, loadClips]);

    const toggle = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = (ids: string[]) => {
        setSelected(prev => {
            const next = new Set(prev);
            const allSel = ids.every(id => next.has(id));
            ids.forEach(id => allSel ? next.delete(id) : next.add(id));
            return next;
        });
    };

    const remaining = quota && !quota.unlimited ? quota.remaining : Infinity;
    const overQuota = selected.size > remaining;

    const submit = async () => {
        if (selected.size === 0 || overQuota) return;
        const count = selected.size;
        setSubmitting(true);
        setError(null);
        try {
            await createUploadClips(uploadId, Array.from(selected));
            setSelected(new Set());
            setJustOrdered(count);
            const q = await getClipQuota();
            setQuota(q);
            await loadClips();
            // The Create button sits at the bottom of a long list — pull the confirmation into view.
            setTimeout(() => successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
        } catch (e: any) {
            setError(e?.response?.data?.error || t("userDemos.clips.createError"));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className={styles.muted}>…</div>;

    const byPlayer = new Map<string, HighlightCandidate[]>();
    for (const c of candidates) {
        if (!byPlayer.has(c.steamId)) byPlayer.set(c.steamId, []);
        byPlayer.get(c.steamId)!.push(c);
    }

    return (
        <div>
            {error && <div className="alert alert-danger">{error}</div>}

            {justOrdered > 0 && (
                <div ref={successRef} className={styles.clipSuccess}>
                    ✓ {t("userDemos.clips.ordered", { n: justOrdered })}
                </div>
            )}

            {clips.length > 0 && (
                <div className="mb-4">
                    <h5 className={styles.clipHeader}>{t("userDemos.clips.yourClips")}</h5>
                    <hr className={styles.clipRule} />
                    <div className={styles.clipGrid}>
                        {clips.map(clip => (
                            <div className={styles.clipCard} key={clip.id}>
                                <video className={styles.clipVideo} src={clip.clipUrl} poster={clip.thumbnailUrl} controls playsInline preload="none" />
                                <div className={styles.clipMeta}>
                                    <span className={`${styles.hlType} ${typeClass(clip.highlightType)}`}>{shortType(clip.highlightType)}</span>
                                    <span className={styles.hlMetaText}>{clip.playerName} · {t("userDemos.clips.round")} {clip.roundNr}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {justOrdered === 0 && jobs.some(isPending) && (
                <div className={styles.clipInfo}>⏳ {t("userDemos.clips.rendering")}</div>
            )}

            {candidates.length === 0 ? (
                <div className={styles.banner}>{t("userDemos.clips.noCandidates")}</div>
            ) : (
                <>
                    <h5 className={styles.clipHeader}>{t("userDemos.clips.pickTitle")}</h5>
                    <hr className={styles.clipRule} />
                    {Array.from(byPlayer.entries()).map(([steamId, list]) => {
                        const name = list[0]?.playerName || steamId;
                        const ids = list.map(c => c.id);
                        const allSel = ids.every(id => selected.has(id));
                        return (
                            <div key={steamId} className={styles.playerGroup}>
                                <div className={styles.playerGroupHeader}>
                                    <span className={styles.playerName}>{name}</span>
                                    {ownSteamId === steamId && <span className={styles.youBadge}>{t("userDemos.clips.you")}</span>}
                                    <button type="button" className={styles.selectAll} onClick={() => toggleAll(ids)}>
                                        {allSel ? t("userDemos.clips.deselectAll") : t("userDemos.clips.selectAll")}
                                    </button>
                                </div>
                                <div className={styles.chipRow}>
                                    {list.map(c => {
                                        const sel = selected.has(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                className={`${styles.hlChip} ${sel ? styles.hlChipActive : ""}`}
                                                onClick={() => toggle(c.id)}
                                            >
                                                <span className={`${styles.hlType} ${typeClass(c.type)}`}>{shortType(c.type)}</span>
                                                <span className={styles.hlMetaText}>{t("userDemos.clips.round")} {c.roundNumber}</span>
                                                {sel && <span className={styles.hlCheck}>✓</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    <div className={styles.stickyBar}>
                        <span className={styles.quotaText}>
                            <span className={styles.quotaStrong}>{selected.size}</span> {t("userDemos.clips.selectedShort")}
                            {!quota?.unlimited && <>
                                {" · "}<span className={styles.quotaStrong}>{quota?.used ?? 0}/{quota?.limit ?? 0}</span> {t("userDemos.clips.usedThisMonth")}
                            </>}
                        </span>
                        <button
                            className="ms-auto"
                            style={{ background: "linear-gradient(135deg,#ff6600,#ff9933)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 700, cursor: "pointer", opacity: (selected.size === 0 || overQuota || submitting) ? 0.5 : 1 }}
                            disabled={selected.size === 0 || overQuota || submitting}
                            onClick={submit}
                        >
                            {submitting ? t("userDemos.clips.creating") : t("userDemos.clips.create", { n: selected.size })}
                        </button>
                        {overQuota && <span className="text-danger small">{t("userDemos.clips.overQuota")}</span>}
                    </div>
                </>
            )}
        </div>
    );
};

export default ClipCreatorPanel;
